import ISessionPersistenceControlStrategy from './persistence/ISessionPersistenceControlStrategy';
import AllSessionChangesStrategy from './persistence/AllSessionChangesStrategy';
import TestController from '../TestController';
import SessionApiController from './api/SessionApiController';
import Test from '@/models/Test';
import Section from '@/models/Section';
import Item from '@/models/Item';
import IStateMapCreatorStrategy from './IStateMapCreatorStrategy';
import StateMapCreator from './StateMapCreator';
import Utility from '@/util/Utility';
import IQtiSession, { IDeliveryOption, ItemInterface, ISection, ITestPart } from './models/IQtiSession';
import DeliveryOptionsProvider from '@/models/DeliveryOptionsProvider';
import CommandPollingManager from '../polling/CommandPollingManager';
import IPauseState from './models/IPauseState';
import { PauseManager, PauseStateValue } from '../pause/PauseManager';
import IPauseStateUpdateHandler from '../pause/IPauseStateUpdateHandler';
import { log, LoggingType } from '@/logging/CustomLogging';
import EventBusConstants from '@/constants/EventBusConstants';
import IPersistenceCommandCreator from '../persistence/IPersistenceCommandCreator';
import IApiCommand from '@/api/IApiCommand';
import DisplayItem from '@/models/DisplayItem';

class SessionController implements IPauseStateUpdateHandler, IPersistenceCommandCreator<IQtiSession> {
  public static readonly STATE_KEY = 'state';

  private readonly qtiSessionId: string;
  private readonly sessionId: string;
  private readonly sessionPersistenceControlStrategy: ISessionPersistenceControlStrategy;
  private readonly testController: TestController;
  private readonly sessionApiController: SessionApiController;
  private _sessionData: IQtiSession | undefined;
  private stateMapCreator: IStateMapCreatorStrategy = new StateMapCreator();
  private _pollingManager: CommandPollingManager<IPauseState> | undefined;

  constructor(qtiSessionId: string, sessionId: string, testController: TestController) {
    this.qtiSessionId = qtiSessionId;
    this.sessionId = sessionId;
    this.testController = testController;
    this.sessionPersistenceControlStrategy = new AllSessionChangesStrategy();
    this.sessionApiController = new SessionApiController(testController!.baseServiceUrl);
  }

  getCommand(item: DisplayItem | undefined | null, ignoreSessionData: boolean): IApiCommand<IQtiSession> | null {
    if (ignoreSessionData) {
      return null;
    }
    this.updateSessionDataFromTest();
    return this.sessionApiController.createPersistenceCommand(this._sessionData);
  }

  get sessionData(): IQtiSession | undefined {
    return this._sessionData;
  }

  initialize(): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return new Promise<void>((resolve, reject) => {
      that.sessionApiController
        .loadSessionData<IQtiSession | null>(that.qtiSessionId)
        .then((sessionData) => {
          if (sessionData) {
            that._sessionData = sessionData;
            that._sessionData.sessionId = that.sessionId;
            that._sessionData.completed = false;
            that._pollingManager = that.sessionApiController.createPollingManager(that.qtiSessionId, that);
          }
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  itemChangedEvent(): void {
    if (this.sessionPersistenceControlStrategy.includeItemUpdated()) {
      this.updateSessionDataFromTest();
      //  this.sessionApiController.persistSession(this.sessionData);
    }
  }

  itemStateChangedEvent(): void {
    if (this.sessionPersistenceControlStrategy.includeItemStateUpdate()) {
      this.updateSessionDataFromTest();
      // this.sessionApiController.persistSession(this.sessionData);
    }
  }

  performFinalSubmit(): void {
    this.testController.test!.completed = true;
    this.updateSessionDataFromTest();
    this.testController.persistenceManager?.save(null, true, false, true);
  }

  updateTestFromSessionData(): void {
    this.setCurrentItem(
      this.testController.test!,
      this.sessionData!.state_map.currentTestPart,
      this.sessionData!.state_map.currentSection,
      this.sessionData!.state_map.currentItem,
      this.sessionData!.state_map.locationState
    );

    this.updateDeliveryOption(this.testController.test!, this.sessionData!.state_map.deliveryOptions);

    this.testController.test!.testParts.forEach((testPart) => {
      const sessionDataTestPart: ITestPart | undefined = this.findSessionDataTestPart(testPart.identifier);
      if (sessionDataTestPart) {
        this.updateDeliveryOption(testPart, sessionDataTestPart.deliveryOptions);
      }

      testPart.sections.forEach((section) => {
        const sessionDataSection: ISection | undefined = this.findSessionDataSection(
          sessionDataTestPart,
          section.identifier
        );
        if (sessionDataSection) {
          this.updateDeliveryOption(section, sessionDataSection.deliveryOptions);
          if (sessionDataSection.state) {
            section.releasedByProctor = Utility.bitIsSet(
              sessionDataSection.state,
              Section.ITEM_RELEASED_BY_PROCTOR_BIT_POSITION
            );
          }
        }

        section.items.forEach((i) => {
          const item: ItemInterface | undefined = this.getSessionDataItem(
            testPart.identifier,
            section.identifier,
            i.identifier
          );
          if (item && item.state) {
            i.isFlagged = Utility.bitIsSet(item.state, Item.ITEM_FLAGGED_BIT_POSITION);
          }
        });
      });
    });
  }

  private updateDeliveryOption(
    deliveryOptionsProvider: DeliveryOptionsProvider,
    sessionDataOptions: Array<IDeliveryOption> | undefined
  ): void {
    if (sessionDataOptions) {
      deliveryOptionsProvider.deliveryOptions.forEach((currentDeliveryOption) => {
        const option = sessionDataOptions.find(
          (sessionDataDeliveryOption: IDeliveryOption) => sessionDataDeliveryOption.name === currentDeliveryOption.name
        );
        if (option && option.settings) {
          currentDeliveryOption.settings.forEach((value: string, key: string) => {
            if (option.settings[key] && option.settings[key].length > 0) {
              currentDeliveryOption.addSetting(key, option.settings[key]);
            }
          });
        }
      });
    }
  }

  private findSessionDataSection(testPart: ITestPart | undefined, identifier: string): ISection | undefined {
    if (testPart && testPart.sections) {
      return testPart.sections.find((section: ISection) => {
        return section.identifier && section.identifier === identifier;
      });
    }

    return undefined;
  }

  private findSessionDataTestPart(identifier: string): ITestPart | undefined {
    if (this.sessionData!.state_map.testParts) {
      return this.sessionData!.state_map.testParts.find((testPart: ITestPart) => {
        return testPart.identifier && testPart.identifier === identifier;
      });
    }
    return undefined;
  }

  private getSessionDataItem(
    testPartIdentifier: string,
    sectionIdentifier: string,
    itemIdentifier: string
  ): ItemInterface | undefined {
    if (this._sessionData!.state_map.testParts) {
      const testPart = this._sessionData!.state_map.testParts.find(
        (testPart) => (testPart.identifier = testPartIdentifier)
      );
      if (testPart) {
        const section = testPart.sections
          ? testPart.sections.find((section: { identifier: string }) => section.identifier === sectionIdentifier)
          : undefined;
        if (section) {
          return section.items
            ? section.items.find((item: ItemInterface) => item.identifier === itemIdentifier)
            : undefined;
        }
      }
    }
    return undefined;
  }

  private setCurrentItem(
    test: Test,
    currentTestPart: string | undefined,
    currentSection: string | undefined,
    currentItemIdentifier: string | undefined,
    locationState: number
  ): void {
    if (currentTestPart && currentSection) {
      const section: Section | undefined = test.testParts
        .map((testPart) => {
          return testPart.sections;
        })
        .flat()
        .find((section) => {
          return section.testPart.identifier === currentTestPart && section.identifier === currentSection;
        });

      if (section) {
        if (currentItemIdentifier) {
          const item: Item | undefined = section.items.find((item) => item.identifier === currentItemIdentifier);
          if (item) {
            item.currentItem = true;
            test.locationState = locationState;
          }
        } else {
          if (section.items && section.items.length > 0) {
            section.items[0].currentItem = true;
            section.items[0].section.testPart.test.locationState = Test.SECTION_INSTRUCTIONS_STATE;
          }
        }
      }
    }
  }

  private updateSessionDataFromTest(): void {
    this._sessionData!.state_map = this.stateMapCreator.createStateMap(this.testController.test!);

    if (this.testController.test!.completed) {
      this._sessionData!.completed = true;
    }
  }

  get pollingManager(): CommandPollingManager<IPauseState> | undefined {
    return this._pollingManager;
  }

  //ICommandPollingNotificationHandler
  commandPollingNotification(data: IPauseState) {
    if (data && data.status === 'ok') {
      const pauseStateValue: PauseStateValue | undefined = PauseManager.stringToPauseStateValue(data.state);
      if (pauseStateValue) {
        this.testController.pauseManager?.handlePauseState(pauseStateValue);
      }
    }
  }

  //ICommandPollingNotificationHandler
  commandPollingNotificationError(error: Error) {
    log('SessionApiController.commandPollingNotificationError: ' + error, LoggingType.ERROR);
    this.testController.playerEmitter.emit(EventBusConstants.NETWORK_ERROR);
  }

  //IPauseStateUpdateHandler
  updatePauseState(state: PauseStateValue): Promise<IPauseState> {
    return this.sessionApiController.updatePauseState(this.qtiSessionId, this.sessionId, state);
  }
}

export default SessionController;
